import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { PieceRow, PieceRowSkeleton } from "./PieceRow";
import { PieceCard, PieceCardSkeleton } from "./PieceCard";
import { useViewMode } from "./useViewMode";
import { Spinner, StateMessage, Button } from "../../components/ui";
import { listStagger } from "../../motion";
import { useMediaRefresh } from "../media/useMediaRefresh";
import { useT } from "../../i18n/strings";
import { messagesOf } from "../../api/errors";
import type { PieceRow as Row } from "../../api/types";
import s from "./pieces.module.css";

interface PieceListProps {
  rows: Row[];
  isLoading: boolean;
  error: unknown;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  empty: React.ReactNode;
}

export function PieceList({
  rows,
  isLoading,
  error,
  hasMore,
  isFetchingMore,
  onLoadMore,
  onRefresh,
  empty,
}: PieceListProps) {
  const t = useT();
  const view = useViewMode();
  const grid = view === "grid";
  const sentinel = useRef<HTMLDivElement>(null);
  // Rows share one cooldown: thirty expired thumbnails are still one refetch.
  const refreshMedia = useMediaRefresh(onRefresh);

  // Cursor pages arrive as the sentinel scrolls into view; no "load more" tap.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && onLoadMore(),
      { rootMargin: "320px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, rows.length]);

  if (isLoading) {
    return (
      <div className={grid ? s.grid : s.list} aria-busy="true">
        {Array.from({ length: grid ? 4 : 6 }, (_, index) =>
          grid ? <PieceCardSkeleton key={index} /> : <PieceRowSkeleton key={index} />,
        )}
      </div>
    );
  }

  if (error) {
    return (
      <StateMessage
        icon="alert"
        title={t.errors.title}
        body={messagesOf(error)[0]}
        action={
          <Button variant="secondary" onClick={onRefresh}>
            {t.actions.retry}
          </Button>
        }
      />
    );
  }

  if (!rows.length) return <>{empty}</>;

  return (
    <>
      <motion.div
        className={grid ? s.grid : s.list}
        variants={listStagger}
        initial="hidden"
        animate="show"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {rows.map((row) =>
            grid ? (
              <PieceCard key={row.id} piece={row} onMediaError={refreshMedia} />
            ) : (
              <PieceRow key={row.id} piece={row} onMediaError={refreshMedia} />
            ),
          )}
        </AnimatePresence>
      </motion.div>

      <div ref={sentinel} className={s.sentinel} />
      {isFetchingMore && (
        <div className={s.footLoad}>
          <Spinner />
        </div>
      )}
    </>
  );
}
